import * as XLSX from "xlsx";

// Parses a bank statement export (CSV or Excel) entirely client-side —
// same reasoning as gstr2-parse.js: keeps the request body to small
// structured JSON instead of shipping the raw file to the backend, and
// every bank formats its export differently (column wording/order,
// separate Debit/Credit columns vs one signed Amount column, an optional
// running-balance column), so this maps by header name, not position,
// exactly like the GSTR-2A/2B parser.

const HEADER_PATTERNS = {
  date: /^(txn|transaction|value|posting)?\s*date$/i,
  description: /narration|description|particulars|details/i,
  referenceNumber: /ref(erence)?\s*(no|number|#)?|cheque\s*(no|number)?|chq/i,
  debit: /debit|withdrawal/i,
  credit: /credit|deposit/i,
  amount: /^amount$/i, // single signed-amount column, used when no separate debit/credit columns exist
  runningBalance: /balance/i,
};

function normalizeHeaderCell(cell) {
  return String(cell ?? "").trim();
}

function findHeaderRow(aoa, maxScanRows = 15) {
  let bestRow = -1;
  let bestScore = 0;

  for (let r = 0; r < Math.min(maxScanRows, aoa.length); r++) {
    const row = aoa[r] || [];
    let score = 0;
    for (const cell of row) {
      const text = normalizeHeaderCell(cell);
      if (!text) continue;
      for (const pattern of Object.values(HEADER_PATTERNS)) {
        if (pattern.test(text)) {
          score++;
          break;
        }
      }
    }
    if (score > bestScore) {
      bestScore = score;
      bestRow = r;
    }
  }

  // Require at least a date column + one of (debit/credit/amount) to
  // trust this is a real header, not a bank's own summary/disclaimer row.
  return bestScore >= 2 ? bestRow : -1;
}

function buildColumnMap(headerRow) {
  const map = {};
  headerRow.forEach((cell, colIndex) => {
    const text = normalizeHeaderCell(cell);
    if (!text) return;
    for (const [field, pattern] of Object.entries(HEADER_PATTERNS)) {
      if (map[field] === undefined && pattern.test(text)) {
        map[field] = colIndex;
      }
    }
  });
  return map;
}

const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);
const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Returns a Date normalized to UTC midnight of the intended calendar date
// — never a local-midnight Date, which toDateOnlyString's UTC-field read
// would then shift backward by a day in any timezone ahead of UTC (e.g.
// IST, this app's only real timezone — see gstr2-parse.js's identical
// fix and reasoning).
function parseDate(value) {
  if (value instanceof Date) {
    return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
  }
  if (typeof value === "number") {
    return new Date(EXCEL_EPOCH_MS + value * MS_PER_DAY);
  }
  // Indian bank CSVs are always DD/MM/YYYY or DD-MM-YYYY — read explicitly
  // as day-first rather than falling back to the JS Date parser (which
  // assumes US MM/DD/YYYY for slash-separated strings and would silently
  // misread every date past the 12th of the month).
  const dmy = String(value).trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    const year = y.length === 2 ? `20${y}` : y;
    const parsed = new Date(Date.UTC(Number(year), Number(m) - 1, Number(d)));
    if (!isNaN(parsed.getTime())) return parsed;
  }
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return null;
  return new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()));
}

// Formats a Date as YYYY-MM-DD reading its UTC fields — the machine-
// readable wire format sent to the backend; see gstr2-parse.js's
// toDateOnlyString for why UTC fields (not local, not .toISOString()
// after further conversion) are the correct read here. User-facing date
// DISPLAY throughout this app is always DD-MM-YYYY (India's convention);
// this value is never shown to a user directly.
function toDateOnlyString(date) {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseNumber(value) {
  if (value === "" || value == null) return 0;
  // Bank exports often wrap debit/credit as "1,234.56" or "(1,234.56)" for
  // negatives, or leave the non-applicable column blank/"-".
  const cleaned = String(value).replace(/[,₹\s]/g, "").replace(/^\((.*)\)$/, "-$1");
  if (cleaned === "-" || cleaned === "") return 0;
  const num = Number(cleaned);
  return isNaN(num) ? 0 : num;
}

// Parses a raw bank statement workbook/CSV (an ArrayBuffer) into
// { rows, errors }. Accepts both .xlsx and .csv — XLSX.read handles both
// via the same API.
export function parseBankStatement(arrayBuffer) {
  const workbook = XLSX.read(arrayBuffer, { type: "array", cellDates: false });
  const firstSheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[firstSheetName];

  const aoa = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: true, defval: "" });
  const headerRowIndex = findHeaderRow(aoa);

  if (headerRowIndex === -1) {
    return { rows: [], errors: [{ row: 0, message: "Could not find a recognizable header row (Date + Debit/Credit/Amount)" }] };
  }

  const columnMap = buildColumnMap(aoa[headerRowIndex]);
  if (columnMap.date === undefined) {
    return { rows: [], errors: [{ row: headerRowIndex + 1, message: "No Date column found" }] };
  }
  if (columnMap.debit === undefined && columnMap.credit === undefined && columnMap.amount === undefined) {
    return { rows: [], errors: [{ row: headerRowIndex + 1, message: "No Debit/Credit/Amount column found" }] };
  }

  const rows = [];
  const errors = [];

  for (let r = headerRowIndex + 1; r < aoa.length; r++) {
    const line = aoa[r];
    if (!line || line.every((c) => c === "" || c == null)) continue;

    const rowNumber = r + 1;
    const date = parseDate(line[columnMap.date]);
    if (!date) {
      errors.push({ row: rowNumber, message: "Invalid/missing date" });
      continue;
    }

    let debit = 0;
    let credit = 0;

    if (columnMap.debit !== undefined || columnMap.credit !== undefined) {
      debit = columnMap.debit !== undefined ? parseNumber(line[columnMap.debit]) : 0;
      credit = columnMap.credit !== undefined ? parseNumber(line[columnMap.credit]) : 0;
    } else if (columnMap.amount !== undefined) {
      // Single signed-amount column: negative = money out (debit),
      // positive = money in (credit) — the universal bank-export
      // convention when debit/credit aren't split into separate columns.
      const amount = parseNumber(line[columnMap.amount]);
      if (amount < 0) debit = -amount;
      else credit = amount;
    }

    if (debit === 0 && credit === 0) continue; // blank/spacer row

    rows.push({
      date: toDateOnlyString(date),
      description: columnMap.description !== undefined ? String(line[columnMap.description] ?? "").trim() : "",
      referenceNumber: columnMap.referenceNumber !== undefined ? String(line[columnMap.referenceNumber] ?? "").trim() : "",
      debit: +debit.toFixed(2),
      credit: +credit.toFixed(2),
      runningBalance: columnMap.runningBalance !== undefined ? parseNumber(line[columnMap.runningBalance]) : null,
    });
  }

  return { rows, errors };
}
